import {supabase} from './supabase';

const ARTWORK_BUCKET='product-artwork';
const STORAGE_PREFIX=`storage://${ARTWORK_BUCKET}/`;
const MAX_ARTWORK_BYTES=6*1024*1024;
const ALLOWED_ARTWORK_TYPES=new Set(['image/jpeg','image/png','image/webp','application/pdf']);

export type ArtworkAttachment={reference:string;fileName:string;mimeType:string};

export const artworkAttachments=(product:any):ArtworkAttachment[]=>{
  const supplied=Array.isArray(product?.artwork_files)?product.artwork_files:[];
  const normalized=supplied.map((item:any)=>({
    reference:String(item?.reference??item?.artwork_key??item?.path??'').trim(),
    fileName:String(item?.fileName??item?.file_name??'Artwork attachment').trim()||'Artwork attachment',
    mimeType:String(item?.mimeType??item?.mime_type??'').trim(),
  })).filter((item:ArtworkAttachment)=>item.reference);
  if(normalized.length)return normalized.slice(0,3);
  const legacyReference=String(product?.artwork_key??product?.artwork_path??product?.artwork_url??'').trim();
  return legacyReference?[{reference:legacyReference,fileName:String(product?.artwork_file_name||'Artwork attachment'),mimeType:''}]:[];
};

export const artworkReference=(product:any)=>artworkAttachments(product)[0]?.reference||'';

export const isStoredArtworkReference=(reference:string)=>reference.startsWith(STORAGE_PREFIX);

const artworkPath=(reference:string)=>isStoredArtworkReference(reference)?reference.slice(STORAGE_PREFIX.length):'';

const safeFileName=(name:string)=>{
  const cleaned=name.normalize('NFKD').replace(/[^a-zA-Z0-9._-]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');
  return cleaned.slice(-100)||'artwork-image';
};

export async function uploadProductArtwork(file:File,companyId:string,branchId:string|null){
  if(!companyId)throw new Error('Your company access could not be confirmed. Please sign out and sign in again.');
  if(!ALLOWED_ARTWORK_TYPES.has(file.type))throw new Error('Please choose a JPG, PNG, WebP or PDF file.');
  if(file.size>MAX_ARTWORK_BYTES)throw new Error('Each artwork file must be 6 MB or smaller.');
  const path=`${companyId}/${branchId||'company'}/${crypto.randomUUID()}-${safeFileName(file.name)}`;
  const{error}=await supabase.storage.from(ARTWORK_BUCKET).upload(path,file,{contentType:file.type,cacheControl:'3600',upsert:false});
  if(error)throw new Error(`Artwork upload failed: ${error.message}`);
  return `${STORAGE_PREFIX}${path}`;
}

export async function getArtworkUrl(reference:string){
  const value=String(reference||'').trim();
  if(/^https?:\/\//i.test(value))return value;
  const path=artworkPath(value);
  if(!path)throw new Error('This artwork reference is not an image or web link.');
  const{data,error}=await supabase.storage.from(ARTWORK_BUCKET).createSignedUrl(path,300);
  if(error||!data?.signedUrl)throw new Error(error?.message||'Unable to open the artwork image.');
  return data.signedUrl;
}

export async function removeProductArtwork(reference:string){
  const path=artworkPath(String(reference||''));
  if(!path)return;
  const{error}=await supabase.storage.from(ARTWORK_BUCKET).remove([path]);
  if(error)throw new Error(`Unable to remove the old artwork image: ${error.message}`);
}
